<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  watch,
  ref,
  nextTick,
  type PropType,
  type Ref,
} from 'vue'
import { i18n } from '@/i18n'
import useStrapiStore from '@/composition/strapiStore'
import useAppStore from '@/composition/appStore'
import type {
  Alternative,
  LikeableResource,
  LikeableResourceGroup,
  Category,
  Page,
  Project,
} from '@/models'
import { Category_List, Alternative_List } from '@/models'
import BlockHeader from '@/components/blockStyles/fields/BlockHeader.vue'
import ProgressBar from '@/components/resources/ProgressBar.vue'
import ResourceGroup from '@/components/resources/ResourceGroup.vue'
import ResourceHeader from '@/components/resources/ResourceHeader.vue'
import SelectedItems from '@/components/resources/SelectedItems.vue'
import CreateProjectDialog from '@/components/resources/CreateProjectDialog.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import { categoryColour } from '@/constants'

import RightArrow from '@/assets/rightArrow.svg'
import What1 from '@/assets/what1.svg'
import What2 from '@/assets/what2.svg'
import What3 from '@/assets/what3.svg'

import Blue from '@/assets/buildcategories/blue.svg'
import Brown from '@/assets/buildcategories/brown.svg'
import Green from '@/assets/buildcategories/green.svg'
import Pink from '@/assets/buildcategories/pink.svg'
import Red from '@/assets/buildcategories/red.svg'
import Yellow from '@/assets/buildcategories/yellow.svg'

const t = i18n.global.t
const strapiStore = useStrapiStore()
const appStore = useAppStore()

defineProps({
  page: {
    type: Object as PropType<Page>,
    required: false,
  },
})

async function getResources() {
  await strapiStore.actions.loadResourceGroups()
  await strapiStore.actions.loadResources()
}

// All Resources and Projects to choose from
const resourceGroups = strapiStore.getters.resourceGroups
const resources = strapiStore.getters.resources
const projects = appStore.getters.projects

// Submenu
const currentTab = ref(0)

// Create
const createDialogActive = ref(false)
const existingProjectNames = computed(() => projects.value.map((p) => p.title))

// Project
const selectedProject = appStore.getters.currentProject
const selectedCategory: Ref<Category | undefined> = ref()
const selectedAlternative: Ref<Alternative | undefined> = ref('one') // For use with the 'What' option only

const tabs = computed(() => [
  {
    id: 0,
    label: () => selectedProject.value?.title,
  },
  {
    id: 1,
    label: () => t('buildSelectedElements'),
  },
])

function createProject(name: string, description: string) {
  const p: Project = {
    id: '',
    title: name,
    description: description,
    lastEdited: new Date(),
    resources: [],
    whatFreeText: '',
  }
  appStore.actions.createProject(p)
  appStore.actions.setCurrentProject(p)
}

function categoryData(category: Category) {
  switch (category) {
    case 'what':
      return { bg: Green, bgWidth: 'w-32', text: t('categoryDescriptionWhat'), stages: 2 }
    case 'how':
      return { bg: Yellow, bgWidth: 'w-28', text: t('categoryDescriptionWhat'), stages: 3 }
    case 'who':
      return { bg: Brown, bgWidth: 'w-40', text: t('categoryDescriptionWhat'), stages: 2 }
    case 'where':
      return { bg: Red, bgWidth: 'w-36', text: t('categoryDescriptionWhat'), stages: 2 }
    case 'when':
      return { bg: Blue, bgWidth: 'w-36', text: t('categoryDescriptionWhat'), stages: 1 }
    case 'challenges & opportunities':
      return { bg: Pink, bgWidth: 'w-24', text: t('categoryDescriptionWhat'), stages: 1 }
    default:
      return { bg: Green, bgWidth: 'w-28', text: t('categoryDescriptionWhat'), stages: 3 }
  }
}

function getAlternativeImage(a: Alternative) {
  if (a === 'one') return What1
  else if (a === 'two') return What2
  else if (a === 'three') return What3
}

const stage = computed(() => Alternative_List.indexOf(selectedAlternative.value ?? 'one'))

onMounted(async () => {
  await getResources()
  setDefaultProject()
})

onUnmounted(async () => {
  appStore.actions.setCurrentProject(undefined)
})

const fullResourceList = computed(() => {
  const groups = resourceGroups.value.values()
  return Array.from(groups)
    .filter((r) => r.category === selectedCategory.value)
    .filter((r) => r.alternative === selectedAlternative.value)
    .map((rg) => {
      const resources: LikeableResource[] = rg.resources.map((r) => ({
        ...r,
        liked: selectedProject.value?.resources.includes(r.documentId) ?? false,
      }))
      const brg: LikeableResourceGroup = {
        ...rg,
        resources,
        liked: selectedProject.value?.resources.includes(rg.documentId) ?? false,
      }
      return brg
    })
})

const builtResourceList = computed(() => {
  if (!selectedProject.value) return []
  return Array.from(resourceGroups.value.values()).map((rg) => {
    const resources: LikeableResource[] = rg.resources
      .filter((r) => selectedProject.value!.resources.includes(r.documentId))
      .map((r) => ({
        ...r,
        liked: true,
      }))
    const brg: LikeableResourceGroup = {
      ...rg,
      resources,
      liked: true,
    }
    return brg
  })
})

function build(rID: string) {
  const toggleState = appStore.actions.toggleResource('build', rID)
  if (!selectedProject.value) return

  // If we liked / unliked a Resource Group, we need to toggle all contained resources
  const resourceGroupLike = resourceGroups.value.get(rID)
  if (resourceGroupLike) {
    resourceGroupLike.resources.forEach((r) => {
      const resourceState = selectedProject.value!.resources.includes(r.documentId)
      if (resourceState && !toggleState) appStore.actions.toggleResource('build', r.documentId)
      else if (!resourceState && toggleState) appStore.actions.toggleResource('build', r.documentId)
    })
    // Else we liked / unliked a Resource, we may need to toggle the ResourceGroup
  } else {
    const resource = resources.value.get(rID)
    if (resource && resourceGroups.value.has(resource.parent.documentId)) {
      const group = resourceGroups.value.get(resource.parent.documentId)!
      const groupIsOn = selectedProject.value!.resources.includes(group.documentId)
      const allResourcesOn = group.resources.every((r) =>
        selectedProject.value!.resources.includes(r.documentId),
      )
      // All resoruces are toggled 'on' but group is not, so also toggle the group to 'on'
      // Or group is 'on' but not all resources are 'on' so toggle the group to 'off'
      if ((!groupIsOn && allResourcesOn) || (groupIsOn && !allResourcesOn)) {
        appStore.actions.toggleResource('build', group.documentId)
      }
    }
  }
}

function updateWhatFreeText() {
  appStore.actions.updateWhatFreeText(whatFreeText.value)
}

const whatFreeText = ref('')

const section = ref<HTMLElement | null>(null)

const scrollToSection = () => {
  section.value?.scrollIntoView({ behavior: 'smooth' })
}

const onCategorySelect = async (c: Category) => {
  selectedCategory.value = c
  selectedAlternative.value = 'one'
  await nextTick()
  scrollToSection()
}

const loggedIn = computed(() => {
  return !!appStore.getters.user.value
})

watch(loggedIn, (isLoggedIn, wasLoggedIn) => {
  if (isLoggedIn && !wasLoggedIn) {
    onUserLoggedIn()
  }
})

function onUserLoggedIn() {
  setDefaultProject()
}

function setDefaultProject() {
  // if no project selected, select the first project
  if (projects.value.length && !selectedProject.value)
    appStore.actions.setCurrentProject(projects.value[0]!)
  //else createProject(t('firstProjectName'), '')
  whatFreeText.value = selectedProject.value?.whatFreeText ?? ''
}
</script>

<template>
  <div class="relative flex flex-col text-black">
    <!-- Creation dialog box -->
    <CreateProjectDialog
      v-model="createDialogActive"
      :existing-project-names="existingProjectNames"
      @create="createProject"
    />

    <!-- Search panel -->
    <ResourceHeader :logged-in="loggedIn" @create-project="createDialogActive = true">
      <template #heading>
        <span class="text-textYellow">{{ t('coCreate') }}&nbsp;</span>{{ t('theFuture') }}
      </template>
      <template #subtitle>{{ t('buildDescription1') }}</template>
    </ResourceHeader>

    <!-- content panel -->
    <div class="text-[#123A35] font-alexandria">
      <div class="max-w-[1512px] sm:px-[5rem] max-sm:px-[2.5rem] mx-auto">
        <!-- Empty state: prompt user to create first collection -->
        <div class="m-8" v-if="!selectedProject">
          <p class="text-xl sm:text-2xl/8 max-w-[96em] font-light">
            {{ t('buildDescription2') }}
          </p>
          <BlockHeader class="mt-10 mb-20" :text="t('buildCreateCollection')" />
        </div>

        <!-- Tab selector: project name vs. selected items -->
        <div
          v-if="loggedIn && selectedProject"
          class="m-8 mb-16 flex flex-row border-b-3 border-grey py-4"
        >
          <div class="flex flex-col sm:flex-row items-end max-sm:w-full">
            <p
              v-for="tab in tabs"
              :key="tab.id"
              class="cursor-pointer max-sm:rounded-full max-sm:px-4 max-sm:py-2 max-sm:w-full sm:mr-8 relative after:content-[''] after:absolute after:left-0 after:-bottom-[19px] after:w-full after:h-[3px]"
              :class="[
                tab.id === 1 && 'ml-5',
                currentTab === tab.id
                  ? 'after:bg-black max-sm:after:bg-transparent sm:text-2xl text-black max-sm:bg-black max-sm:text-white font-normal'
                  : 'after:bg-transparent sm:text-xl text-grey font-light',
              ]"
              @pointerdown="currentTab = tab.id"
            >
              {{ tab.label() }}
            </p>
          </div>
        </div>

        <!-- Choose Items to Build -->
        <div v-if="currentTab === 0">
          <!-- Category selection: grid of clickable cards with color backgrounds -->
          <div
            class="mb-20 relative z-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-10 sm:gap-x-10 sm:gap-y-20"
          >
            <div
              v-for="c of Category_List"
              :key="c"
              class="relative rounded-lg px-8 pt-10 pb-7 flex flex-col min-w-16 sm:min-h-72 lg:min-w-72 cursor-pointer shadow-md after:content-[''] after:absolute after:inset-[-32px] after:blur-[48px] after:opacity-20 after:-z-10"
              :class="[
                selectedCategory == c ? categoryColour(selectedCategory).background : 'bg-white',
                selectedCategory == c ? categoryColour(selectedCategory).text : 'text-[#123A35]',
                categoryColour(c).after,
              ]"
              @pointerdown="onCategorySelect(c)"
            >
              <h2 class="text-xl sm:text-3xl font-bold overflow-hidden text-ellipsis">
                {{ t(c) }}
              </h2>
              <p class="pt-4 font-medium">{{ t(c + 'Description') }}</p>
              <div class="flex flex-col grow justify-end">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 44.26 34.11"
                  class="w-5 sm:w-6 pt-6 ml-3 mb-3"
                  :class="[selectedCategory == c ? [categoryColour(selectedCategory).text] : '']"
                >
                  <path
                    fill="currentColor"
                    stroke="currentColor"
                    stroke-width="3"
                    d="M25.37,1.54s.06-.04.12-.04.1.02.11.04l16.47,15.52-16.47,15.52s-.06.04-.11.04c-.03,0-.05,0-.07,0l-.04-.03-.75-.7,14.57-13.72,1.16-1.09-1.16-1.09L24.62,2.24l.75-.7Z"
                  />
                  <line
                    x1="0"
                    y1="16.97"
                    x2="38.33"
                    y2="16.97"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                </svg>
              </div>
              <img
                :src="categoryData(c).bg"
                :class="[
                  categoryData(c).bgWidth,
                  selectedCategory === c ? 'mix-blend-multiply' : '',
                ]"
                class="absolute bottom-0 right-0"
              />
            </div>
          </div>
          <hr
            ref="section"
            v-if="selectedCategory"
            class="border-t-1 relative left-1/2 right-1/2 -ml-[50vw] max-w-[100dvw] border-[#E8DCDC]"
          />
          <div v-if="selectedCategory">
            <!-- Category description -->
            <div class="flex flex-col m-8 mt-20">
              <p class="text-l sm:text-xl/8 max-w-[96em]">
                <span class="font-bold text-xl sm:text-2xl">{{ t(selectedCategory ?? '') }}</span
                >&nbsp;{{ categoryData(selectedCategory).text }}
              </p>
              <BlockHeader class="mt-8" :text="t(selectedCategory ?? '')" />
            </div>
            <!-- Alternative boxes for 'what' category, or staged progress bar for others -->
            <template v-if="selectedCategory === 'what'">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-5 mb-20">
                <div
                  v-for="a of Alternative_List"
                  :key="a"
                  class="rounded-2xl border-4 p-4 sm:p-8 text-[#8A8A8A] flex flex-col items-center cursor-pointer shadow-md"
                  :class="{
                    'border-[#8A8A8A]': selectedAlternative == a,
                    'outline-1 outline-[#DBD2D2] border-transparent': selectedAlternative != a,
                  }"
                  @pointerdown="selectedAlternative = a"
                >
                  <img
                    :src="getAlternativeImage(a)"
                    class="w-10 sm:w-20 pb-4"
                    :class="{
                      invert: selectedAlternative == a,
                    }"
                  />
                  <h2 class="text-xl sm:text-2xl text-center">{{ t(`what-alternative-${a}`) }}</h2>
                </div>
              </div>
            </template>
            <template v-else>
              <ProgressBar
                :category="selectedCategory"
                :stage="stage"
                :total="categoryData(selectedCategory).stages"
              />
            </template>

            <!-- Filtered resources: shown based on category and stage selection -->
            <div class="w-full mt-6">
              <ResourceGroup
                v-for="rg of fullResourceList"
                class="mt-4"
                :key="rg.documentId"
                :category="selectedCategory"
                :stage="stage"
                :resourceGroup="rg"
                :liked="rg.liked"
                :like-mode="selectedProject ? 'build' : 'none'"
                :mode="'build'"
                @like="build"
              />
            </div>

            <!-- Free text input appears only for open-ended 'what' stage -->
            <div v-if="selectedCategory === 'what' && selectedAlternative === 'three'">
              <textarea
                class="bg-white w-full p-2 rounded-2xl min-h-60"
                :placeholder="t('typeYourThoughts')"
                v-model="whatFreeText"
                @input="updateWhatFreeText"
              />
            </div>

            <!-- Stage navigation: skip 'what' category which uses alternatives instead -->
            <div
              v-if="selectedCategory !== 'what'"
              class="w-full flex flex-row justify-between mt-16"
            >
              <div
                v-if="stage > 0"
                @click="selectedAlternative = Alternative_List[stage - 1]"
                class="w-12 h-12 rounded-full flex items-center justify-center cursor-pointer bg-black place-self-start"
              >
                <img :src="RightArrow" class="w-4 h-4 invert rotate-180" />
              </div>
              <div
                v-if="stage < 2 && stage < categoryData(selectedCategory).stages - 1"
                @click="selectedAlternative = Alternative_List[stage + 1]"
                class="w-12 h-12 ml-auto rounded-full flex items-center justify-center cursor-pointer bg-black place-self-end"
              >
                <img :src="RightArrow" class="w-4 h-4 invert" />
              </div>
            </div>
          </div>
        </div>

        <!-- User-Selected Items -->
        <SelectedItems
          v-else-if="currentTab === 1"
          :liked-resource-list="builtResourceList"
          :mode="'build'"
        />
      </div>
      <SiteFooter class="mt-32" />
    </div>
  </div>
</template>

<style scoped></style>
